import { useMemo } from "react"
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type { EquityPoint } from "../types/backtest"
import { cn } from "@/lib/utils"

export interface EquityCurveProps {
  data: EquityPoint[]
  initialBalance?: number
  title?: string
  subtitle?: string
  realisedLabel?: string
  equityLabel?: string
  realisedColor?: string
  className?: string
  emptyLabel?: string
  /**
   * Optional playback-sync props. Activated when `rangeStartTs` /
   * `rangeEndTs` are provided AND data points carry a numeric `time`. In
   * that mode the X axis becomes a unix-seconds numeric scale pinned to
   * the backtest range, a vertical reference line tracks `currentTime`,
   * and clicks on the chart invoke `onTimeClick`.
   */
  boundaryTime?: number
  currentTime?: number
  rangeStartTs?: number
  rangeEndTs?: number
  onTimeClick?: (time: number) => void
}

function formatShortDate(unixSeconds: number): string {
  if (!Number.isFinite(unixSeconds)) return ""
  const d = new Date(unixSeconds * 1000)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })
}

export function EquityCurve({
  data,
  initialBalance = 10000,
  title = "Gain (%)",
  subtitle = "Realised gain vs floating equity over the period",
  realisedLabel = "Realised",
  equityLabel = "Equity",
  realisedColor = "#26a69a",
  className,
  emptyLabel = "Not enough data in this period",
  boundaryTime,
  currentTime,
  rangeStartTs,
  rangeEndTs,
  onTimeClick,
}: EquityCurveProps) {
  // Time-axis mode is opt-in: it requires both the backtest range and at
  // least one data point with a numeric `time`. When off, the component
  // behaves exactly as before (string `label` axis, no marker, no scrub).
  const useTimeAxis =
    rangeStartTs != null
    && rangeEndTs != null
    && data.length > 0
    && typeof data[0].time === "number"

  // Trim by playback boundary (no-op for points without a `time`).
  const trimmedData = useMemo(() => {
    if (boundaryTime == null) return data
    return data.filter((p) => p.time == null || p.time <= boundaryTime)
  }, [data, boundaryTime])

  // Categorical mode keeps the original "empty" fallback so the prototype
  // and Storybook continue to render the placeholder card. In time-axis
  // mode we still want the empty frame to line up with the candle range
  // while we wait for the first equity point to stream in.
  if (!useTimeAxis && trimmedData.length < 2) {
    return (
      <div
        className={cn(
          "border-b border-border p-4 flex items-center justify-center text-caption text-muted-foreground h-[304px]",
          className,
        )}
      >
        {emptyLabel}
      </div>
    )
  }

  const formatPercent = (v: number) =>
    `${v >= 0 ? "+" : ""}${((v / initialBalance) * 100).toFixed(1)}%`

  // Recharts' onClick payload type is awkward to express precisely; the only
  // field we touch is the active item's underlying datum.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChartClick = onTimeClick
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? (state: any) => {
        const t = state?.activePayload?.[0]?.payload?.time
        if (typeof t === "number") onTimeClick(t)
      }
    : undefined

  return (
    <div className={cn("border-b border-border p-4", className)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex flex-col gap-1">
          <div className="text-section text-foreground">{title}</div>
          <div className="text-caption text-muted-foreground">{subtitle}</div>
        </div>
        <div className="flex items-center gap-3 text-caption text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-0.5 rounded-full"
              style={{ backgroundColor: realisedColor }}
            />
            {realisedLabel}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 border-t border-dashed border-muted-foreground" />
            {equityLabel}
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <AreaChart
          data={trimmedData}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          onClick={handleChartClick}
          style={onTimeClick ? { cursor: "crosshair" } : undefined}
        >
          <defs>
            <linearGradient id="eqGainFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={realisedColor} stopOpacity={0.15} />
              <stop offset="100%" stopColor={realisedColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          {useTimeAxis ? (
            <XAxis
              type="number"
              dataKey="time"
              domain={[rangeStartTs as number, rangeEndTs as number]}
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              axisLine={{ stroke: "var(--border)" }}
              tickLine={false}
              tickFormatter={formatShortDate}
              allowDuplicatedCategory={false}
              scale="time"
            />
          ) : (
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              axisLine={{ stroke: "var(--border)" }}
              tickLine={false}
            />
          )}
          <YAxis
            tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
            width={56}
            axisLine={{ stroke: "var(--border)" }}
            tickLine={false}
            tickFormatter={formatPercent}
          />
          <Tooltip
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              fontSize: 11,
              color: "var(--foreground)",
              padding: "6px 8px",
            }}
            labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
            labelFormatter={useTimeAxis
              ? (label) => (typeof label === "number" ? formatShortDate(label) : "")
              : undefined}
            formatter={(value) =>
              typeof value === "number"
                ? Math.round(value).toLocaleString("en-US")
                : String(value ?? "")
            }
            cursor={{
              stroke: "var(--muted-foreground)",
              strokeWidth: 1,
              strokeDasharray: "3 3",
            }}
          />
          <Area
            type="monotone"
            dataKey="realised"
            stroke={realisedColor}
            strokeWidth={1.5}
            fill="url(#eqGainFill)"
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="equity"
            stroke="var(--muted-foreground)"
            strokeWidth={1}
            strokeDasharray="4 3"
            fill="none"
            isAnimationActive={false}
          />
          {useTimeAxis && currentTime != null && (
            <ReferenceLine
              x={currentTime}
              stroke="var(--muted-foreground)"
              strokeDasharray="3 3"
              strokeWidth={1}
              ifOverflow="hidden"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
